import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Zap, Activity } from 'lucide-react';

const GameLoader = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('INITIALIZING SYSTEMS');
  const [glitchActive, setGlitchActive] = useState(false);

  const loadingStages = [
    { progress: 0, text: 'INITIALIZING SYSTEMS' },
    { progress: 25, text: 'LOADING DRONE PROTOCOLS' },
    { progress: 50, text: 'CALIBRATING SENSORS' },
    { progress: 75, text: 'ESTABLISHING CONNECTIONS' },
    { progress: 100, text: 'READY FOR LAUNCH' },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(() => onComplete && onComplete(), 300);
          return 100;
        }
        
        const nextStage = loadingStages.find(stage => stage.progress > prev);
        if (nextStage && prev + 10 >= nextStage.progress) {
          setLoadingText(nextStage.text);
        }
        
        return prev + Math.random() * 15;
      });
    }, 100);

    // Random glitch effect
    const glitchInterval = setInterval(() => {
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), 100);
    }, 3000);

    return () => {
      clearInterval(interval);
      clearInterval(glitchInterval);
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black flex items-center justify-center overflow-hidden"
      data-testid="game-loader"
    >
      {/* Cyberpunk grid background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Animated grid */}
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(0, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 255, 255, 0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
          animation: 'grid-move 20s linear infinite'
        }} />
        
        {/* Neon scanlines */}
        <div className="absolute inset-0" style={{
          backgroundImage: 'repeating-linear-gradient(0deg, rgba(0, 255, 255, 0.03) 0px, transparent 2px, transparent 4px)',
          animation: 'scanline 8s linear infinite'
        }} />

        {/* Vertical neon bars */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-0.5 bg-gradient-to-b from-transparent via-primary to-transparent opacity-30"
            style={{
              left: `${(i * 5) + 2.5}%`,
              height: '100%',
            }}
            initial={{ scaleY: 0 }}
            animate={{ scaleY: [0, 1, 0] }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: i * 0.1,
            }}
          />
        ))}

        {/* Floating particles */}
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={`particle-${i}`}
            className="absolute w-1 h-1 bg-primary rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}

        {/* Corner brackets */}
        <div className="absolute top-8 left-8 w-20 h-20 border-t-2 border-l-2 border-primary/50" />
        <div className="absolute top-8 right-8 w-20 h-20 border-t-2 border-r-2 border-secondary/50" />
        <div className="absolute bottom-8 left-8 w-20 h-20 border-b-2 border-l-2 border-accent/50" />
        <div className="absolute bottom-8 right-8 w-20 h-20 border-b-2 border-r-2 border-primary/50" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-2xl w-full px-8 flex flex-col items-center justify-center">
        {/* Logo with cyberpunk effects */}
        <motion.div
          initial={{ scale: 0, rotate: -180, opacity: 0 }}
          animate={{ scale: 1, rotate: 0, opacity: 1 }}
          transition={{ duration: 0.8, type: 'spring', bounce: 0.5 }}
          className="relative mb-2 md:mb-3"
        >
          {/* Multiple glowing layers */}
          <div className="absolute inset-0 bg-primary/40 blur-3xl rounded-full animate-pulse" />
          <div className="absolute inset-0 bg-secondary/30 blur-2xl rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
          <div className="absolute inset-0 bg-accent/20 blur-xl rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
          
          {/* Rotating hexagon frame */}
          <motion.div
            className="absolute inset-0 border-2 border-primary/30"
            style={{
              clipPath: 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)'
            }}
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          />

          {/* Logo Container */}
          <div className={`relative w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 lg:w-72 lg:h-72 flex items-center justify-center ${glitchActive ? 'animate-glitch' : ''}`}>
            <img 
              src="/assets/logo.png" 
              alt="Oman Drone Carnival Logo" 
              className="w-full h-full object-contain drop-shadow-[0_0_50px_rgba(0,255,255,0.9)]" 
              style={{
                filter: glitchActive ? 'hue-rotate(180deg) saturate(200%)' : 'none'
              }}
            />
          </div>

          {/* Orbiting dots */}
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={`orbit-${i}`}
              className="absolute w-2 h-2 bg-primary rounded-full"
              style={{
                top: '50%',
                left: '50%',
              }}
              animate={{
                x: [0, 150 * Math.cos((i * 120 * Math.PI) / 180)],
                y: [0, 150 * Math.sin((i * 120 * Math.PI) / 180)],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'linear',
                delay: i * 1,
              }}
            />
          ))}
        </motion.div>

        {/* Title with glitch effect */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="relative mb-6 md:mb-8"
        >
          {/* Glitch layers */}
          {glitchActive && (
            <>
              <h1 className="absolute inset-0 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-black text-center uppercase tracking-tighter leading-tight text-primary blur-sm" style={{ transform: 'translate(-2px, 0)' }}>
                OMAN DRONE CARNIVAL
              </h1>
              <h1 className="absolute inset-0 text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-black text-center uppercase tracking-tighter leading-tight text-secondary blur-sm" style={{ transform: 'translate(2px, 0)' }}>
                OMAN DRONE CARNIVAL
              </h1>
            </>
          )}
          
          <h1 className="relative text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-black text-center uppercase tracking-tighter leading-tight">
            <span className="bg-gradient-to-r from-primary via-white to-secondary bg-clip-text text-transparent drop-shadow-[0_0_20px_rgba(0,255,255,0.5)]">
              OMAN DRONE CARNIVAL
            </span>
          </h1>

          {/* Underline accent */}
          <motion.div
            className="h-0.5 bg-gradient-to-r from-transparent via-primary to-transparent mx-auto mt-2"
            initial={{ width: 0 }}
            animate={{ width: '80%' }}
            transition={{ delay: 0.5, duration: 0.8 }}
          />
        </motion.div>

        {/* Loading section with cyberpunk styling */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="w-full max-w-md space-y-3"
        >
          {/* Status header with icons */}
          <div className="flex items-center justify-between border border-primary/30 bg-primary/5 px-3 py-2 backdrop-blur-sm">
            <div className="flex items-center space-x-2">
              <Activity className="h-3 w-3 text-primary animate-pulse" />
              <span className="text-xs sm:text-sm font-mono uppercase tracking-wider text-primary">
                {loadingText}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-xs sm:text-sm font-mono text-accent font-bold">
                {Math.floor(progress)}%
              </span>
              <Zap className="h-3 w-3 text-accent" />
            </div>
          </div>

          {/* Cyberpunk progress bar */}
          <div className="relative">
            {/* Outer frame */}
            <div className="relative h-4 sm:h-5 border-2 border-primary/50 bg-black/80 overflow-hidden backdrop-blur-sm"
              style={{
                clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))'
              }}
            >
              {/* Progress fill */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-primary via-accent to-secondary"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.2 }}
              >
                {/* Animated shine effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-[shimmer_1.5s_infinite]" />
                
                {/* Digital blocks overlay */}
                <div className="absolute inset-0 opacity-30" style={{
                  backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 10px, rgba(255,255,255,0.2) 10px, rgba(255,255,255,0.2) 11px)',
                }} />
              </motion.div>

              {/* Static overlay */}
              <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)',
              }} />
            </div>

            {/* Corner accents */}
            <div className="absolute -top-1 -left-1 w-3 h-3 border-t-2 border-l-2 border-primary" />
            <div className="absolute -top-1 -right-1 w-3 h-3 border-t-2 border-r-2 border-accent" />
            <div className="absolute -bottom-1 -left-1 w-3 h-3 border-b-2 border-l-2 border-secondary" />
            <div className="absolute -bottom-1 -right-1 w-3 h-3 border-b-2 border-r-2 border-primary" />
          </div>

          {/* Tech details with cyberpunk styling */}
          <div className="flex items-center justify-center space-x-2 sm:space-x-3 text-[10px] sm:text-xs font-mono text-primary/70 pt-2 border-t border-primary/20">
            <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 animate-spin text-primary" />
            <span className="px-2 py-0.5 bg-primary/10 border border-primary/30">SYS v2.0</span>
            <span className="w-1.5 h-1.5 bg-accent rounded-full animate-pulse" />
            <span className="px-2 py-0.5 bg-accent/10 border border-accent/30">MUSCAT</span>
            <span className="w-1.5 h-1.5 bg-secondary rounded-full animate-pulse" />
            <span className="px-2 py-0.5 bg-secondary/10 border border-secondary/30">OMAN</span>
          </div>
        </motion.div>
      </div>

      {/* Animated CSS */}
      <style jsx>{`
        @keyframes grid-move {
          0% { transform: translate(0, 0); }
          100% { transform: translate(50px, 50px); }
        }
        @keyframes scanline {
          0% { transform: translateY(0); }
          100% { transform: translateY(100vh); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes glitch {
          0% { transform: translate(0); }
          20% { transform: translate(-2px, 2px); }
          40% { transform: translate(-2px, -2px); }
          60% { transform: translate(2px, 2px); }
          80% { transform: translate(2px, -2px); }
          100% { transform: translate(0); }
        }
        .animate-glitch {
          animation: glitch 0.3s infinite;
        }
      `}</style>
    </motion.div>
  );
};

export default GameLoader;